import { useState, useEffect } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  History,
  Users,
  Phone,
  CreditCard,
  Menu,
  X,
  Sun,
  Moon,
  LogOut,
  ChevronLeft,
  ChevronRight,
  Workflow,
  ContactRound,
  Handshake,
} from 'lucide-react';
import { ConfirmDialog } from '@/components/shared/ConfirmDialog';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useAuth } from '@/providers/AuthProvider';
import { BalanceProvider } from '@/providers/BalanceProvider';
import { useTheme } from '@/providers/ThemeProvider';
import CreditsPill from './CreditsPill';
import LowBalanceBanner from './LowBalanceBanner';
import { OnboardingChip } from './OnboardingChip';

interface NavItem {
  to: string;
  label: string;
  icon: typeof LayoutDashboard;
  /** When set, only render the link when the predicate returns true.
   *  Predicate receives the current AuthUser; absent items always
   *  render. Used for the channel-partner "Partners" link which only
   *  appears for tagged tenants. */
  visibleWhen?: (user: { isPartner: boolean } | null) => boolean;
}

const navItems: NavItem[] = [
  { to: '/', label: 'Dashboard', icon: LayoutDashboard },
  { to: '/agents', label: 'Agents', icon: Users },
  { to: '/contacts', label: 'Contacts', icon: ContactRound },
  // The route is `/campaigns` (the underlying entity name in the DB +
  // server routes); the user-facing label is "Sequences" because that's
  // the product naming. Don't rename the URL — every existing call_log,
  // campaign_target, and audit row references `campaigns.*`.
  { to: '/campaigns', label: 'Sequences', icon: Workflow },
  // /dnc is folded into /contacts as a special list. Standalone link
  // removed. The route remains as a redirect for old bookmarks.
  { to: '/calls', label: 'Call History', icon: History },
  { to: '/twilio', label: 'Phone Numbers', icon: Phone },
  { to: '/billing', label: 'Billing', icon: CreditCard },
  {
    to: '/partners',
    label: 'Partner Corner',
    icon: Handshake,
    visibleWhen: (u) => Boolean(u?.isPartner),
  },
];

const SIDEBAR_COLLAPSED_WIDTH = 68;
const SIDEBAR_EXPANDED_WIDTH = 280;

const pageTitles: Record<string, string> = {
  '/': 'Dashboard',
  '/agents': 'Agents',
  '/welcome': 'Create Agent',
  '/agents/new': 'Create Agent',
  '/contacts': 'Contacts',
  '/campaigns': 'Sequences',
  '/calls': 'Call History',
  '/twilio': 'Twilio Accounts',
  '/billing': 'Billing',
  '/partners': 'Partner Corner',
  '/admin': 'Admin',
  '/profile': 'Profile',
};

function getPageTitle(pathname: string): string {
  if (pageTitles[pathname]) return pageTitles[pathname];
  if (/^\/agents\/[^/]+\/edit$/.test(pathname)) return 'Edit Agent';
  // Legacy /voice and /phone now redirect to /playground (PR-B);
  // resolve the title for all three so the brief redirect bounce
  // doesn't flash the old labels.
  if (/^\/agents\/[^/]+\/(voice|phone|playground)$/.test(pathname)) {
    return 'Playground';
  }
  if (/^\/campaigns\/[^/]+$/.test(pathname)) return 'Sequence';
  return 'Meridian';
}

export default function Layout() {
  const location = useLocation();
  const navigate = useNavigate();
  const { isDark, toggleTheme } = useTheme();
  const { user, logout } = useAuth();

  const pageTitle = getPageTitle(location.pathname);

  const [collapsed, setCollapsed] = useState(() => {
    try {
      return localStorage.getItem('meridian-sidebar') === 'collapsed';
    } catch {
      return false;
    }
  });
  const [mobileOpen, setMobileOpen] = useState(false);
  // Logout confirm — the Logout button sits visually adjacent to
  // Collapse / Theme / Profile in the sidebar footer cluster, so a
  // misclick used to silently sign the user out with no warning.
  // The dialog is the single safety gate.
  const [logoutConfirmOpen, setLogoutConfirmOpen] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);

  useEffect(() => {
    try {
      localStorage.setItem('meridian-sidebar', collapsed ? 'collapsed' : 'expanded');
    } catch {
      /* noop */
    }
  }, [collapsed]);

  // Close mobile drawer on route change — event-driven via nav clicks
  // All NavLinks and Link components include onClick={() => setMobileOpen(false)}

  const handleLogout = async () => {
    setLoggingOut(true);
    try {
      await logout();
      navigate('/login');
    } finally {
      setLoggingOut(false);
      setLogoutConfirmOpen(false);
    }
  };

  const sidebarW = collapsed ? SIDEBAR_COLLAPSED_WIDTH : SIDEBAR_EXPANDED_WIDTH;
  const expanded = !collapsed || mobileOpen;

  const avatarInitial = (user?.email?.charAt(0) ?? 'U').toUpperCase();

  return (
    <BalanceProvider>
      <div className="min-h-screen th-bg">
        {/* Mobile overlay */}
        {mobileOpen && (
          <div
            className="fixed inset-0 z-40 bg-black/40 backdrop-blur-sm lg:hidden"
            onClick={() => setMobileOpen(false)}
          />
        )}

        {/* Sidebar — always dark toned for visual contrast */}
        <aside
          className={`fixed top-0 left-0 z-50 h-full flex flex-col th-bg-sidebar sidebar-transition overflow-hidden
          ${mobileOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0`}
          style={{
            width: mobileOpen ? SIDEBAR_EXPANDED_WIDTH : sidebarW,
            borderRight: '1px solid var(--th-sidebar-border)',
          }}
        >
          {/* Logo */}
          <div
            className={`flex items-center h-16 shrink-0 ${!expanded ? 'justify-center px-0' : 'px-2'}`}
            style={{ borderBottom: '1px solid var(--th-sidebar-border)' }}
          >
            <NavLink
              to="/"
              onClick={() => setMobileOpen(false)}
              className="flex items-center shrink-0"
            >
              <img
                src="/otaru-logo-white.png"
                alt="Otaru AI"
                className="w-14 h-14 shrink-0 object-contain"
              />
              {expanded && (
                <div className="overflow-hidden">
                  {/* Single-line brand lockup — sidebar has ~210 px of
                    runway between the logo and the right edge when
                    expanded (280 - 56 logo - ~14 padding), which comfortably
                    fits "Otaru AI Meridian" at text-base. */}
                  <span className="text-base font-bold text-white tracking-tight whitespace-nowrap leading-tight">
                    Otaru AI Meridian
                  </span>
                </div>
              )}
            </NavLink>
            {mobileOpen && (
              <button
                onClick={() => setMobileOpen(false)}
                className="ml-auto p-1.5 rounded-lg text-white/40 hover:text-white/70 transition-colors"
              >
                <X size={16} />
              </button>
            )}
          </div>

          {/* Nav — Radix Tooltip when collapsed (portals to <body>, escapes
            the aside's overflow-hidden which clips any sibling pill).
            When expanded the label renders inline and the tooltip is
            skipped. `asChild` targets the flex-span wrapper, NOT the
            NavLink, so NavLink's function-className is untouched. */}
          <TooltipProvider delayDuration={80}>
            <nav className="flex-1 py-3 px-2 space-y-0.5 overflow-y-auto">
              {navItems
                .filter((item) => !item.visibleWhen || item.visibleWhen(user))
                .map(({ to, label, icon: Icon }) => {
                  const link = (
                    <NavLink
                      to={to}
                      end={to === '/' || to === '/agents' || to === '/calls'}
                      onClick={() => setMobileOpen(false)}
                      className={({ isActive }) => {
                        const base =
                          'flex items-center gap-3 rounded-xl text-[13px] font-medium transition-all duration-150';
                        const pad = !expanded
                          ? 'justify-center px-0 py-2.5 mx-auto w-11 h-11'
                          : 'px-3 py-2.5';
                        const colors = isActive
                          ? 'bg-blue-500/15 text-blue-300'
                          : 'text-white/50 hover:bg-white/[0.06] hover:text-white/80';
                        return `${base} ${pad} ${colors}`;
                      }}
                    >
                      <Icon size={18} className="shrink-0" />
                      {expanded && <span>{label}</span>}
                    </NavLink>
                  );
                  if (expanded) return <div key={to}>{link}</div>;
                  return (
                    <Tooltip key={to}>
                      <TooltipTrigger asChild>
                        <span className="flex justify-center">{link}</span>
                      </TooltipTrigger>
                      <TooltipContent side="right" sideOffset={8}>
                        {label}
                      </TooltipContent>
                    </Tooltip>
                  );
                })}
            </nav>
          </TooltipProvider>

          {/* Bottom */}
          <div
            className="py-3 px-2 space-y-0.5 shrink-0"
            style={{ borderTop: '1px solid var(--th-sidebar-border)' }}
          >
            {/* Profile / Theme / Logout / Collapse — same portaled-tooltip
              pattern as the top-nav items when collapsed. */}
            <TooltipProvider delayDuration={80}>
              {(() => {
                const profileLink = (
                  <NavLink
                    to="/profile"
                    onClick={() => setMobileOpen(false)}
                    className={({ isActive }) => {
                      const base =
                        'flex items-center gap-3 rounded-xl text-[13px] font-medium transition-all duration-150';
                      const pad = !expanded
                        ? 'justify-center px-0 py-2.5 mx-auto w-11 h-11'
                        : 'px-3 py-2.5 w-full';
                      const colors = isActive
                        ? 'bg-violet-500/15 text-violet-300'
                        : 'text-white/50 hover:bg-white/[0.06] hover:text-white/80';
                      return `${base} ${pad} ${colors}`;
                    }}
                  >
                    {/* Same 5x5 footprint as the lucide icons above so the
                      "Profile" label lines up with the other rows. */}
                    <div className="w-5 h-5 rounded-full flex items-center justify-center shrink-0 bg-gradient-to-br from-violet-500 to-cyan-400">
                      <span className="text-white text-[10px] font-bold leading-none select-none">
                        {avatarInitial}
                      </span>
                    </div>
                    {expanded && <span>Profile</span>}
                  </NavLink>
                );
                const themeLabel = isDark ? 'Switch to light mode' : 'Switch to dark mode';
                const themeButton = (
                  <button
                    onClick={toggleTheme}
                    className={`flex items-center gap-3 rounded-xl text-[13px] font-medium transition-all duration-150 text-white/50
                    ${!expanded ? 'justify-center mx-auto w-11 h-11' : 'px-3 py-2.5 w-full'}
                    hover:bg-amber-500/10 hover:text-amber-300`}
                  >
                    {isDark ? (
                      <Sun size={18} className="shrink-0 text-amber-400" />
                    ) : (
                      <Moon size={18} className="shrink-0 text-indigo-400" />
                    )}
                    {expanded && <span>{isDark ? 'Light Mode' : 'Dark Mode'}</span>}
                  </button>
                );
                const logoutButton = (
                  <button
                    onClick={() => setLogoutConfirmOpen(true)}
                    className={`flex items-center gap-3 rounded-xl text-[13px] font-medium transition-all duration-150 text-white/50
                    ${!expanded ? 'justify-center mx-auto w-11 h-11' : 'px-3 py-2.5 w-full'}
                    hover:bg-red-500/10 hover:text-red-400`}
                  >
                    <LogOut size={18} className="shrink-0" />
                    {expanded && <span>Logout</span>}
                  </button>
                );
                const collapseLabel = collapsed ? 'Expand sidebar' : 'Collapse sidebar';
                const collapseButton = (
                  <button
                    onClick={() => setCollapsed((c) => !c)}
                    className={`hidden lg:flex items-center gap-3 rounded-xl text-[13px] font-medium transition-all duration-150 text-white/30
                    ${collapsed ? 'justify-center mx-auto w-11 h-11' : 'px-3 py-2.5 w-full'}
                    hover:bg-white/5 hover:text-white/60`}
                  >
                    {collapsed ? (
                      <ChevronRight size={18} className="shrink-0" />
                    ) : (
                      <ChevronLeft size={18} className="shrink-0" />
                    )}
                    {!collapsed && <span>Collapse</span>}
                  </button>
                );

                function wrap(node: JSX.Element, label: string, showTooltip: boolean) {
                  if (!showTooltip) return node;
                  return (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span className="flex justify-center">{node}</span>
                      </TooltipTrigger>
                      <TooltipContent side="right" sideOffset={8}>
                        {label}
                      </TooltipContent>
                    </Tooltip>
                  );
                }

                return (
                  <>
                    {wrap(profileLink, 'Profile', !expanded)}
                    {wrap(themeButton, themeLabel, !expanded)}
                    {wrap(logoutButton, 'Logout', !expanded)}
                    {wrap(collapseButton, collapseLabel, collapsed)}
                  </>
                );
              })()}
            </TooltipProvider>
          </div>
        </aside>

        {/* Main content */}
        <div className="content-transition" style={{ paddingLeft: `${sidebarW}px` }}>
          <header
            className={`sticky top-0 z-30 h-14 border-b th-border flex items-center justify-between gap-4 px-4 sm:px-6
          ${isDark ? 'bg-[#0c0c0e]/80' : 'bg-[#f5f5f3]/80'} backdrop-blur-xl`}
          >
            <div className="flex items-center gap-3 min-w-0">
              <button
                onClick={() => setMobileOpen(true)}
                className="lg:hidden p-2 -ml-2 rounded-lg th-text-muted hover:th-text transition-colors"
              >
                <Menu size={18} />
              </button>
              <div className="flex items-center gap-2 min-w-0">
                <h2 className="text-sm font-semibold th-text truncate">{pageTitle}</h2>
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <OnboardingChip />
              <CreditsPill />
            </div>
          </header>
          <LowBalanceBanner />
          <main className="p-4 sm:p-6 lg:p-8">
            <Outlet />
          </main>
        </div>

        <ConfirmDialog
          open={logoutConfirmOpen}
          onOpenChange={setLogoutConfirmOpen}
          title="Log out of Meridian?"
          description="You'll need to sign in again to access agents, calls, and contacts."
          confirmLabel={loggingOut ? 'Logging out…' : 'Log out'}
          variant="danger"
          loading={loggingOut}
          onConfirm={handleLogout}
        />
      </div>
    </BalanceProvider>
  );
}
